import { addDoubleSlashToUrl } from './add-double-slash-to-url';

type SlateText = { text: string; bold?: boolean; italic?: boolean; underline?: boolean };
type SlateElement = {
  type?: 'paragraph' | 'numbered-list' | 'bulleted-list' | 'list-item' | 'link';
  href?: string;
  children?: SlateNode[];
};
type SlateNode = SlateText | SlateElement;

const isTextNode = (node: SlateNode): node is SlateText => 'text' in node;

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function serializeText(node: SlateText): string {
  let html = escapeHtml(node.text);

  // Mirror the legacy editor's leaf rendering: bold → <strong>, italic → <em>, underline → <u>.
  if (node.bold) html = `<strong>${html}</strong>`;
  if (node.italic) html = `<em>${html}</em>`;
  if (node.underline) html = `<u>${html}</u>`;

  return html;
}

// Lists wrap their entries in <li>. Stray text/non-list-item children are wrapped defensively.
function serializeListItems(children: SlateNode[]): string {
  return children
    .map((child) =>
      !isTextNode(child) && child.type === 'list-item' ? serializeNode(child) : `<li>${serializeNode(child)}</li>`
    )
    .join('');
}

function serializeNode(node: SlateNode): string {
  if (isTextNode(node)) return serializeText(node);

  const children = node.children ?? [];

  switch (node.type) {
    case 'bulleted-list':
      return `<ul>${serializeListItems(children)}</ul>`;
    case 'numbered-list':
      return `<ol>${serializeListItems(children)}</ol>`;
    case 'list-item':
      return `<li>${children.map(serializeNode).join('')}</li>`;
    case 'link': {
      const inner = children.map(serializeNode).join('');
      const href = addDoubleSlashToUrl(node.href);
      // Malformed legacy data: a link with no usable href would render as <a href=""> and navigate
      // to the current page on click — render the text without an anchor instead.
      if (!href) return inner;
      return `<a href="${escapeHtml(href)}" target="_blank" rel="noreferrer">${inner}</a>`;
    }
    case 'paragraph':
    default:
      return `<p>${children.map(serializeNode).join('')}</p>`;
  }
}

// A Slate node is either a text leaf (`text: string`) or an element (`children: []`). Requiring one
// of these keeps unrelated JSON (e.g. `[{"foo":"bar"}]`) from being treated as a Slate document and
// silently serialized to empty HTML — it falls through to the raw-string passthrough instead.
function isSlateNode(node: unknown): node is SlateNode {
  if (typeof node !== 'object' || node === null) return false;
  const hasText = 'text' in node && typeof (node as SlateText).text === 'string';
  const hasChildren = 'children' in node && Array.isArray((node as SlateElement).children);
  return hasText || hasChildren;
}

function isSlateDocument(value: unknown): value is SlateNode[] {
  return Array.isArray(value) && value.every(isSlateNode);
}

/**
 * Read-time adapter for the RichTextEditor → CL TextEditor migration.
 *
 * The legacy Slate-based editor stored content as Slate JSON (`Descendant[]` serialized with
 * `JSON.stringify`); the CL TextEditor consumes/emits HTML. This converts stored Slate JSON to HTML
 * so existing documents open without data loss, and passes through content that is already HTML —
 * so once a document is re-saved (as HTML) it keeps rendering correctly.
 *
 * @param stored Raw stored content: serialized Slate JSON (legacy) or an HTML string (migrated).
 * @returns HTML string suitable for `<TextEditor value>` / sanitized read-only rendering.
 */
export function slateToHtml(stored?: string | null): string {
  if (!stored) return '';

  // HTML (already migrated) never starts with `[`; only serialized Slate documents do.
  if (!stored.trimStart().startsWith('[')) return stored;

  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return stored;
  }

  if (!isSlateDocument(parsed)) return stored;

  return parsed.map(serializeNode).join('');
}
